import React, { useState } from 'react';
import useAuth from '../../hooks/useAuth';
import { Mail, RefreshCw } from 'lucide-react';
import { BRAND_NAME } from '../../config/branding';
import { getFriendlyAuthError } from '../../lib/firebaseErrors';
import { auth } from '../../firebase';

/**
 * Full-page email verification step shown after sign-up when the user is
 * authenticated but email is not yet verified. Provides clear instructions,
 * resend, and go-to-login option.
 */
const EmailVerificationPage: React.FC = () => {
  const { user, sendVerificationEmail, logout } = useAuth();
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fall back to the Firebase currentUser email: right after sign-up the
  // useAuth UserProfile can briefly have a null email, which rendered the
  // "We sent a verification link to ." line with a blank address.
  const email = user?.email || auth?.currentUser?.email || '';

  const onResend = async () => {
    setError(null);
    setResendLoading(true);
    try {
      await sendVerificationEmail();
      setResendSent(true);
    } catch (err: unknown) {
      // Map Firebase code to a friendly message. Cast to any only to read
      // the `code` field - `err.message` is intentionally NOT used as the
      // fallback because it contains the raw "Firebase: Error (...)" string
      // for unmapped codes.
      const code = (err as { code?: string } | null | undefined)?.code;
      setError(
        getFriendlyAuthError(code, 'Failed to send verification email. Please try again.')
      );
    } finally {
      setResendLoading(false);
    }
  };

  const onGoToLogin = async () => {
    try {
      await logout();
    } catch {
      // ignore
    }
  };

  return (
    <div
      className="d-flex justify-content-center align-items-center"
      style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
      }}
    >
      <div
        className="card p-4"
        style={{
          maxWidth: 440,
          width: '90%',
          backgroundColor: '#1e222d',
          border: '1px solid #363a45',
        }}
      >
        <div className="text-center mb-4">
          <img
            src={`${process.env.PUBLIC_URL || ''}/logo-app.png`}
            alt={BRAND_NAME}
            className="d-block mx-auto mb-2"
            style={{ width: 64, height: 64, objectFit: 'contain' }}
          />
          <h3 className="text-lg font-semibold text-text-primary mb-0.5">
            {BRAND_NAME}
          </h3>
          <p className="mb-0 text-sm text-text-muted">
            AI-powered stock screening
          </p>
        </div>

        <div className="text-center mb-3">
          <Mail size={40} color="#14b8a6" className="mb-2" aria-hidden />
          <h5 className="text-base font-semibold text-text-primary mb-2">
            Verify your email
          </h5>
          <p className="text-sm text-text-secondary mb-0">
            We sent a verification link to{' '}
            <strong className="text-text-primary">{email}</strong>. Check your
            inbox and click the link to verify your account.
          </p>
        </div>

        {resendSent && (
          <div
            className="alert alert-success mb-3"
            style={{
              backgroundColor: 'rgba(34, 197, 94, 0.2)',
              borderColor: '#22c55e',
              color: '#e2e8f0',
            }}
            role="alert"
          >
            Verification email sent again. Check your inbox.
          </div>
        )}
        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            {error}
          </div>
        )}

        <div className="d-flex flex-column gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center gap-2"
            style={{ borderColor: '#334155', color: '#cbd5e1' }}
            onClick={onResend}
            disabled={resendLoading}
          >
            <RefreshCw
              size={16}
              className={resendLoading ? 'email-verification-spin' : ''}
            />
            {resendLoading ? 'Sending…' : 'Resend verification email'}
          </button>
          <button
            type="button"
            className="btn w-100 d-flex align-items-center justify-content-center gap-2"
            style={{
              backgroundColor: '#14b8a6',
              borderColor: '#14b8a6',
              color: '#fff',
            }}
            onClick={onGoToLogin}
          >
            Return to Sign-in
          </button>
        </div>

        <p className="text-center text-text-muted small mt-4 mb-0">
          You can sign in after verifying.
        </p>
      </div>
      <style>{`
        @keyframes email-verification-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .email-verification-spin { animation: email-verification-spin 1s linear infinite; }
      `}</style>
    </div>
  );
};

export default EmailVerificationPage;
